// TypeScript types for the feedback management system
// Extends the auto-generated Supabase types with frontend-specific interfaces

import { Database } from '@/integrations/supabase/types';

// Database types
export type FeedbackCategory = Database['public']['Tables']['feedback_categories']['Row'];
export type FeedbackStatus = Database['public']['Tables']['feedback_statuses']['Row'];
export type FeedbackItem = Database['public']['Tables']['feedback_items']['Row'];
export type FeedbackVote = Database['public']['Tables']['feedback_votes']['Row'];
export type FeedbackComment = Database['public']['Tables']['feedback_comments']['Row'];
export type KanbanBoard = Database['public']['Tables']['kanban_boards']['Row'];
export type KanbanColumn = Database['public']['Tables']['kanban_columns']['Row'];
export type FeedbackBoardItem = Database['public']['Tables']['feedback_board_items']['Row'];
export type ChangelogEntry = Database['public']['Tables']['changelog_entries']['Row'];

// Insert types for creating new records
export type FeedbackItemInsert = Database['public']['Tables']['feedback_items']['Insert'];
export type FeedbackVoteInsert = Database['public']['Tables']['feedback_votes']['Insert'];
export type FeedbackCommentInsert = Database['public']['Tables']['feedback_comments']['Insert'];
export type ChangelogEntryInsert = Database['public']['Tables']['changelog_entries']['Insert'];

// Update types for modifying records
export type FeedbackItemUpdate = Database['public']['Tables']['feedback_items']['Update'];
export type FeedbackCommentUpdate = Database['public']['Tables']['feedback_comments']['Update'];

// =============================================
// FRONTEND-SPECIFIC TYPES
// =============================================

// Enhanced feedback item with joined data
export interface FeedbackItemWithDetails extends FeedbackItem {
  category: FeedbackCategory;
  status: FeedbackStatus;
  user_vote?: number; // Current user's vote (-1, 0, 1)
  author_profile?: {
    username: string;
    full_name: string;
    avatar_url: string;
  };
}

// Feedback comment with author info and threading
export interface FeedbackCommentWithDetails extends FeedbackComment {
  author_profile: {
    username: string;
    full_name: string;
    avatar_url: string;
  };
  replies?: FeedbackCommentWithDetails[];
  reply_count?: number;
}

// Kanban board with columns and items
export interface KanbanBoardWithDetails extends KanbanBoard {
  columns: KanbanColumnWithItems[];
}

export interface KanbanColumnWithItems extends KanbanColumn {
  items: FeedbackBoardItemWithDetails[];
}

export interface FeedbackBoardItemWithDetails extends FeedbackBoardItem {
  feedback_item: FeedbackItemWithDetails;
  assigned_user?: {
    username: string;
    full_name: string;
    avatar_url: string;
  };
}

// Changelog entry with author info
export interface ChangelogEntryWithDetails extends ChangelogEntry {
  author_profile: {
    username: string;
    full_name: string;
    avatar_url: string;
  };
  related_feedback?: FeedbackItemWithDetails[];
}

// =============================================
// FORM AND INPUT TYPES
// =============================================

export interface CreateFeedbackForm {
  title: string;
  description: string;
  category_id: number;
  attachments?: File[];
}

export interface UpdateFeedbackForm {
  title?: string;
  description?: string;
  category_id?: number;
}

export interface CreateCommentForm {
  content: string;
  parent_id?: number;
}

export interface CreateChangelogForm {
  title: string;
  content: string;
  version?: string;
  type: 'feature' | 'improvement' | 'bugfix' | 'update';
  is_featured?: boolean;
  related_feedback_ids?: number[];
}

// =============================================
// API RESPONSE TYPES
// =============================================

export interface VoteResponse {
  success: boolean;
  new_vote_count: number;
  user_vote: number;
  message: string;
}

export interface CreateFeedbackResponse {
  success: boolean;
  feedback_id?: number;
  feedback_slug?: string;
  message: string;
}

export interface CreateCommentResponse {
  success: boolean;
  comment_id?: number;
  message: string;
}

export interface ModerateResponse {
  success: boolean;
  message: string;
}

export interface FeedbackStats {
  total_feedback: number;
  published_feedback: number;
  pending_feedback: number;
  total_votes: number;
  total_comments: number;
  active_users: number;
}

// =============================================
// FILTER AND SEARCH TYPES
// =============================================

export interface FeedbackFilters {
  category_id?: number;
  status_id?: number;
  search?: string;
  sort_by?: 'newest' | 'oldest' | 'most_voted' | 'most_commented';
  show_featured_only?: boolean;
  author_id?: string;
}

export interface FeedbackPagination {
  page: number;
  limit: number;
  total: number;
  has_more: boolean;
}

export interface FeedbackListResponse {
  items: FeedbackItemWithDetails[];
  pagination: FeedbackPagination;
  filters: FeedbackFilters;
}

// =============================================
// REAL-TIME TYPES
// =============================================

export interface FeedbackRealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: FeedbackItem;
  old?: FeedbackItem;
}

export interface VoteRealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: FeedbackVote;
  old?: FeedbackVote;
}

export interface CommentRealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: FeedbackComment;
  old?: FeedbackComment;
}

export interface KanbanRealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: FeedbackBoardItem;
  old?: FeedbackBoardItem;
}

// =============================================
// COMPONENT PROPS TYPES
// =============================================

export interface FeedbackCardProps {
  feedback: FeedbackItemWithDetails;
  showVoting?: boolean;
  showComments?: boolean;
  onClick?: (feedback: FeedbackItemWithDetails) => void;
  className?: string;
}

export interface VotingComponentProps {
  feedbackId: number;
  initialVoteCount: number;
  initialUserVote?: number;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export interface CommentThreadProps {
  feedbackId: number;
  comments: FeedbackCommentWithDetails[];
  onReply?: (parentId: number, content: string) => void;
  onEdit?: (commentId: number, content: string) => void;
  onDelete?: (commentId: number) => void;
  maxDepth?: number;
}

export interface KanbanCardProps {
  item: FeedbackBoardItemWithDetails;
  isDragging?: boolean;
  onEdit?: (item: FeedbackBoardItemWithDetails) => void;
  onAssign?: (item: FeedbackBoardItemWithDetails, userId: string) => void;
}

export interface KanbanColumnProps {
  column: KanbanColumnWithItems;
  onDrop?: (itemId: number, columnId: number, newIndex: number) => void;
  onAddItem?: (columnId: number) => void;
}

// =============================================
// DRAG AND DROP TYPES
// =============================================

export interface DragItem {
  id: number;
  type: 'feedback_item';
  feedback_id: number;
  current_column_id: number;
  current_index: number;
}

export interface DropResult {
  feedback_id: number;
  source_column_id: number;
  target_column_id: number;
  source_index: number;
  target_index: number;
}

// =============================================
// ERROR TYPES
// =============================================

export interface FeedbackError {
  code: string;
  message: string;
  field?: string;
}

export interface ValidationError extends FeedbackError {
  field: string;
}

// =============================================
// UTILITY TYPES
// =============================================

export type FeedbackActionType = 
  | 'vote' 
  | 'comment' 
  | 'edit' 
  | 'delete' 
  | 'moderate' 
  | 'feature' 
  | 'move';

export type PermissionLevel = 'none' | 'read' | 'comment' | 'moderate' | 'admin';

export interface UserPermissions {
  can_view: boolean;
  can_vote: boolean;
  can_comment: boolean;
  can_create: boolean;
  can_edit_own: boolean;
  can_delete_own: boolean;
  can_moderate: boolean;
  can_admin: boolean;
}

// Vote value constants
export const VOTE_VALUES = {
  DOWNVOTE: -1,
  NEUTRAL: 0,
  UPVOTE: 1,
} as const;

export type VoteValue = typeof VOTE_VALUES[keyof typeof VOTE_VALUES];

// Feedback priorities for kanban
export const FEEDBACK_PRIORITIES = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4,
} as const;

export type FeedbackPriority = typeof FEEDBACK_PRIORITIES[keyof typeof FEEDBACK_PRIORITIES];

// Changelog types
export const CHANGELOG_TYPES = {
  FEATURE: 'feature',
  IMPROVEMENT: 'improvement',
  BUGFIX: 'bugfix',
  UPDATE: 'update',
} as const;

export type ChangelogType = typeof CHANGELOG_TYPES[keyof typeof CHANGELOG_TYPES];